import React, { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import {
  Loader2,
  Users,
  CheckCircle,
  XCircle,
  MessageCircle,
  Trash2,
  Download,
  LogOut,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "admin_pw_v1";

const C = {
  bg: "#0F1115",
  bgAlt: "#161719",
  cream: "#F5EBD8",
  creamSoft: "#EFE1C7",
  ink: "#1F2521",
  sage: "#9BB09A",
  sageDark: "#465B48",
  gold: "#A88338",
  goldSoft: "#C6A566",
  red: "#B87A6C",
};

function Stat({ label, value, Icon, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 border"
      style={{ background: C.bgAlt, borderColor: `${C.gold}44`, color: C.cream }}
      data-testid={`admin-stat-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <p
          className="text-[10px] tracking-[0.32em] uppercase"
          style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
        >
          {label}
        </p>
        <Icon size={18} style={{ color }} />
      </div>
      <p
        className="mt-3 leading-none"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "2.5rem",
          color: C.cream,
        }}
      >
        {value}
      </p>
    </motion.div>
  );
}

function LoginGate({ onLogin }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(STORAGE_KEY, pw);
      onLogin(pw);
      toast.success("Bienvenidos");
    } catch (err) {
      toast.error("Contraseña incorrecta");
      setPw("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: C.bg }}
    >
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm p-8 border"
        style={{
          background: C.bgAlt,
          borderColor: `${C.gold}55`,
          color: C.cream,
        }}
        data-testid="admin-login-form"
      >
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-4 flex items-center justify-center h-14 w-14 rounded-full"
            style={{ border: `1px solid ${C.gold}`, color: C.gold }}
          >
            <Lock size={20} />
          </div>
          <p
            className="text-[10px] tracking-[0.4em] uppercase"
            style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
          >
            Panel Privado
          </p>
          <h1
            className="mt-3"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "1.6rem",
              fontStyle: "italic",
            }}
          >
            Confirmaciones
          </h1>
        </div>

        <label
          className="block text-[10px] tracking-[0.32em] uppercase mb-2"
          style={{ color: C.sage }}
        >
          Contraseña
        </label>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="w-full bg-transparent border-b py-2 focus:outline-none"
          style={{
            borderColor: `${C.gold}55`,
            color: C.cream,
            fontFamily: "'Montserrat', sans-serif",
          }}
          data-testid="admin-password-input"
          autoFocus
          placeholder="••••••••"
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full mt-8 py-3 text-[11px] uppercase tracking-[0.32em] flex items-center justify-center gap-2 disabled:opacity-60"
          style={{
            background: C.gold,
            color: C.bg,
            fontFamily: "'Montserrat', sans-serif",
          }}
          data-testid="admin-login-submit"
        >
          {loading && <Loader2 size={14} className="animate-spin" />}
          {loading ? "Verificando…" : "Ingresar"}
        </button>
      </motion.form>
    </div>
  );
}

function Dashboard({ pw, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ confirmed: 0, declined: 0, total_guests: 0 });
  const headers = { "x-admin-password": pw };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        axios.get(`${API}/admin/rsvps`, { headers }),
        axios.get(`${API}/rsvp/stats`),
      ]);
      setItems(list.data || []);
      setStats(s.data || { confirmed: 0, declined: 0, total_guests: 0 });
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        toast.error("Sesión expirada");
      } else {
        toast.error("No se pudieron cargar los datos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`¿Eliminar la confirmación de ${name}?`)) return;
    try {
      await axios.delete(`${API}/admin/rsvps/${id}`, { headers });
      toast.success("Eliminada");
      fetchAll();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const exportCsv = () => {
    // Trigger download via fetch (fetch to include the password header)
    fetch(`${API}/admin/rsvps/export`, { headers })
      .then((r) => {
        if (!r.ok) throw new Error("err");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "rsvps.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("No se pudo exportar"));
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: C.bg, color: C.cream }}
      data-testid="admin-dashboard"
    >
      <header
        className="px-6 py-5 border-b flex items-center justify-between"
        style={{ borderColor: `${C.gold}22`, background: C.bgAlt }}
      >
        <div>
          <p
            className="text-[10px] tracking-[0.4em] uppercase"
            style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
          >
            Panel Privado
          </p>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "1.4rem",
              fontStyle: "italic",
              marginTop: "0.15rem",
            }}
          >
            Dulce & José Manuel
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.32em]"
            style={{
              background: C.sageDark,
              color: C.cream,
              fontFamily: "'Montserrat', sans-serif",
            }}
            data-testid="admin-export-btn"
          >
            <Download size={12} />
            Exportar CSV
          </button>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.32em]"
            style={{
              border: `1px solid ${C.gold}55`,
              color: C.cream,
              fontFamily: "'Montserrat', sans-serif",
            }}
            data-testid="admin-logout-btn"
          >
            <LogOut size={12} />
            Salir
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Asistirán" value={stats.confirmed} Icon={CheckCircle} color={C.sage} />
          <Stat label="No podrán" value={stats.declined} Icon={XCircle} color={C.red} />
          <Stat label="Total Invitados" value={stats.total_guests} Icon={Users} color={C.gold} />
        </div>

        <div className="mt-8 sm:hidden">
          <button
            onClick={exportCsv}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.32em]"
            style={{ background: C.sageDark, color: C.cream }}
            data-testid="admin-export-btn-mobile"
          >
            <Download size={12} />
            Exportar CSV
          </button>
        </div>

        <section className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1.25rem",
                fontStyle: "italic",
              }}
            >
              Lista de Confirmaciones ({items.length})
            </h2>
            <button
              onClick={fetchAll}
              className="text-[10px] uppercase tracking-[0.32em] px-3 py-1"
              style={{
                border: `1px solid ${C.gold}55`,
                color: C.cream,
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              Refrescar
            </button>
          </div>

          {loading ? (
            <div className="text-center py-16 opacity-70">
              <Loader2 className="mx-auto animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div
              className="text-center py-16 border"
              style={{ borderColor: `${C.gold}22`, color: C.creamSoft }}
            >
              Aún no hay confirmaciones.
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((r) => (
                <li
                  key={r.id}
                  className="p-4 sm:p-5 border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  style={{
                    background: C.bgAlt,
                    borderColor: `${C.gold}22`,
                    color: C.cream,
                  }}
                  data-testid={`admin-row-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        style={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: "1.1rem",
                        }}
                      >
                        {r.name}
                      </span>
                      <span
                        className="text-[10px] px-2 py-1 uppercase tracking-[0.28em]"
                        style={{
                          background:
                            r.attendance === "yes" ? C.sageDark : "#8B4A44",
                          color: C.cream,
                          fontFamily: "'Montserrat', sans-serif",
                        }}
                      >
                        {r.attendance === "yes"
                          ? `Sí · ${r.guests || 1}`
                          : "No"}
                      </span>
                    </div>
                    {r.message ? (
                      <div className="mt-2 flex items-start gap-2 text-xs" style={{ color: C.creamSoft }}>
                        <MessageCircle size={12} style={{ color: C.sage, marginTop: 2 }} />
                        <span className="italic">{r.message}</span>
                      </div>
                    ) : null}
                    <p
                      className="mt-1 text-[10px] tracking-[0.2em] uppercase"
                      style={{ color: C.sage, fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString("es-MX", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(r.id, r.name)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.32em] self-start sm:self-auto"
                    style={{
                      border: `1px solid ${C.red}88`,
                      color: C.red,
                    }}
                    data-testid={`admin-delete-${r.id}`}
                  >
                    <Trash2 size={12} />
                    Eliminar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

export default function AdminPanel() {
  const [pw, setPw] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPw("");
  };

  return (
    <>
      {pw ? <Dashboard pw={pw} onLogout={logout} /> : <LoginGate onLogin={setPw} />}
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: C.bgAlt,
            color: C.cream,
            border: `1px solid ${C.gold}66`,
          },
        }}
      />
    </>
  );
}
